import { Box, Button, ButtonGroup, Dialog, Fab, Paper, TableContainer } from "@mui/material"
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { Localized, useLocalization } from "@fluent/react"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { selectSettings } from "../../settings/settingsSlice"
import { unwrapResult } from "@reduxjs/toolkit"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { loadSystemSettings, SystemSetting } from "./systemSettingsApi"
import { CreateEditSystemSettingsDialog } from "./CreateEditSystemSettingsDialog"
import { DeleteSystemSettingsDialog } from "./DeleteSystemSettingsDialog"
import AddIcon from '@mui/icons-material/Add'
import produce from "immer"
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"

type DialogState =
    | { type: "none" }
    | { type: "createEdit", systemSetting: SystemSetting | undefined }
    | { type: "delete", systemSettingId: string }

export const SystemSettings = () => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const dispatch = useAppDispatch();
    const [systemSettings, setSystemSettings] = useState<SystemSetting[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const [dialog, setDialog] = useState<DialogState>({ type: "none" });

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadSystemSettings())
            .then(unwrapResult)
            .then(setSystemSettings)
            .catch((error) => console.error(error))
            .finally(() => setIsLoading(false));
    }, [dispatch])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const DialogContentDispatcher = ({ state, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
        const onSuccess = useCallback((systemSetting: SystemSetting | string) => {
            setter({ type: "none" })
            setSystemSettings(
                produce((draft) => {
                    if (draft !== undefined) {
                        if(typeof systemSetting === "string") {
                            const existingIndex = draft.findIndex((group) => group.id === systemSetting);
                            draft.splice(existingIndex, 1);
                        } else {
                            const existingIndex = draft.findIndex((group) => group.id === systemSetting.id);

                            if (existingIndex !== -1) {
                                draft[existingIndex] = systemSetting;
                            } else {
                                draft.push(systemSetting);
                            }
                        }
                    }
                })
            )
        }, [setter, dispatch])

        const onCancel = useCallback(() => {
            setter({ type: "none" })
        }, [setter])

        switch (state.type) {
            case "none":
                return null

            case "createEdit":
                return <CreateEditSystemSettingsDialog
                    mode={state.systemSetting ? "edit" : "create"}
                    systemSetting={state.systemSetting}
                    onSaveSuccess={onSuccess}
                    onCancel={onCancel}
                />
            case "delete":
                return <DeleteSystemSettingsDialog
                    systemSettingId={state.systemSettingId}
                    onSaveSuccess={onSuccess}
                    onCancel={onCancel}
                />
        }
        }

    const handleAddSystemSettingClick = useCallback(() => {
        setDialog({
            type: "createEdit",
            systemSetting: undefined
        })
    }, [setDialog])

    const handleEditSystemSettingClick = useCallback((systemSetting: SystemSetting) => {
        setDialog({
            type: "createEdit",
            systemSetting
        })
    }, [setDialog])

    const handleDeleteSystemSettingClick = useCallback((id: string) => {
        setDialog({
            type: "delete",
            systemSettingId: id
        })
    }, [setDialog])

    const columns: GridColDef<SystemSetting, any, any>[] = [
        {
            field: 'type',
            headerName: l10n.getString("type", null, "_Typ"),
            width: 300
        },
        {
            field: 'code',
            headerName: l10n.getString("system-settings-code", null, "_Kod"),
            flex: 1,
        },
        {
            field: 'value',
            headerName: l10n.getString("system-settings-value", null, "_Wartość"),
            flex: 1,
        },
        {
            field: 'createDate',
            headerName: l10n.getString("create-date", null, "_Data utworzenia"),
            flex: 1,
            valueGetter: (_, row) => row.createDate ? new Date(row.createDate).toLocaleString() : null
        },
        {
            field: 'actions',
            type: 'actions',
            width: 250,
            renderCell: (params) => {
                return (
                    <ButtonGroup size="small">
                        <Button
                            style={{ width: 100 }}
                            color="secondary"
                            variant="outlined"
                            onClick={() => handleEditSystemSettingClick(params.row)}
                        >
                            <Localized id="edit">
                                <span>_Edytuj</span>
                            </Localized>
                        </Button>
                        <Button
                            style={{ width: 100 }}
                            color="error"
                            variant="outlined"
                            onClick={() => handleDeleteSystemSettingClick(params.row.id)}
                        >
                            <Localized id="delete">
                                <span>_Usuń</span>
                            </Localized>
                        </Button>
                    </ButtonGroup>
                )
            }
        }
    ]

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    return (
        <>
            <TableContainer component={Paper}>
                <DataGrid localeText={getCustomLocaleText()}
                    style={{ minHeight: 100, height: 'calc(100vh - 275px)' }}
                    rows={systemSettings}
                    columns={columns}
                    initialState={{
                        sorting: { sortModel: [{ field: 'eventDate', sort: 'desc' }] }
                    }}
                    slots={{ toolbar: QuickSearchToolbar }}
                />
                <Box sx={{
                    display: 'flex',
                }}>
                    <Fab sx={{
                        margin: 1,
                    }}
                    onClick={() => handleAddSystemSettingClick()} color="secondary" size="small" >
                        <AddIcon />
                    </Fab>
                </Box>
            </TableContainer>
            <Dialog open={dialog.type !== "none"} fullWidth maxWidth="sm" keepMounted={false}>
                <DialogContentDispatcher state={dialog} setter={setDialog} />
            </Dialog>
        </>
    )
}
